import { ActionContext } from 'vuex';

import { leaseIngestionService } from '@/bundles/LeaseIngestion/factory/leaseIngestionServiceFactory';
import {
  ILeaseIngestion,
  IListFilters,
  IUploadLeaseFormFields,
  IUploadLeaseResponse,
  IPatchExtractionRequest,
} from '@/bundles/LeaseIngestion/types';

import { ILeaseIngestionStore, buildListingKey } from './index';
import { LeaseIngestionActionsEnum, LeaseIngestionMutationsEnum } from './lease-ingestion-types';

type Ctx = ActionContext<ILeaseIngestionStore, unknown>;

export default {
  async [LeaseIngestionActionsEnum.FetchList] ({ commit }: Ctx, filter: IListFilters): Promise<ILeaseIngestion[]> {
    const key = buildListingKey(filter);
    commit(LeaseIngestionMutationsEnum.SetListingLoading, { key, loading: true });
    commit(LeaseIngestionMutationsEnum.SetListingError, { key, error: null });

    try {
      const records = await leaseIngestionService.list(filter);

      commit(LeaseIngestionMutationsEnum.UpsertRecords, records);
      commit(LeaseIngestionMutationsEnum.SetListing, {
        key,
        listing: { ids: records.map((r) => r._key), loading: false, error: null },
      });

      return records;
    } catch (error: any) {
      commit(LeaseIngestionMutationsEnum.SetListingLoading, { key, loading: false });
      commit(LeaseIngestionMutationsEnum.SetListingError, { key, error: error?.message ?? 'Failed to load leases' });
      throw error;
    }
  },

  async [LeaseIngestionActionsEnum.FetchOne] ({ commit }: Ctx, id: string): Promise<ILeaseIngestion> {
    commit(LeaseIngestionMutationsEnum.SetDetailLoading, true);
    commit(LeaseIngestionMutationsEnum.SetDetailError, null);

    try {
      const record = await leaseIngestionService.one(id);
      commit(LeaseIngestionMutationsEnum.UpsertRecord, record);
      return record;
    } catch (error: any) {
      commit(LeaseIngestionMutationsEnum.SetDetailError, error?.message ?? 'Failed to load lease');
      throw error;
    } finally {
      commit(LeaseIngestionMutationsEnum.SetDetailLoading, false);
    }
  },

  [LeaseIngestionActionsEnum.Upload] (_: Ctx, fields: IUploadLeaseFormFields): Promise<IUploadLeaseResponse> {
    return leaseIngestionService.upload(fields);
  },

  async [LeaseIngestionActionsEnum.PatchExtraction] (
    { commit }: Ctx,
    { id, body }: { id: string; body: IPatchExtractionRequest },
  ): Promise<ILeaseIngestion> {
    const updated = await leaseIngestionService.patchExtraction(id, body);
    commit(LeaseIngestionMutationsEnum.UpsertRecord, updated);
    return updated;
  },

  async [LeaseIngestionActionsEnum.Reject] (
    { commit }: Ctx,
    { id, reason }: { id: string; reason: string },
  ): Promise<ILeaseIngestion> {
    const updated = await leaseIngestionService.reject(id, { reason });
    commit(LeaseIngestionMutationsEnum.UpsertRecord, updated);
    return updated;
  },

  async [LeaseIngestionActionsEnum.Approve] (
    { commit }: Ctx,
    payload: { id: string; comment?: string; importProperty?: boolean; importOccupier?: boolean; importRentSchedule?: boolean; importComparable?: boolean },
  ): Promise<ILeaseIngestion> {
    const { id, ...approveBody } = payload;
    const updated = await leaseIngestionService.approve(id, approveBody);
    commit(LeaseIngestionMutationsEnum.UpsertRecord, updated);
    return updated;
  },
};
